import {
  mediaHelper,
  paletteHelper,
  radiusHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import { FunctionComponent } from 'react';
import { styled } from 'styled-components';
import { PageContainer } from '../../../components/PageContainer/PageContainer';
import bannerImage from '../../../assets/miele/photo.jpg';
import logoImage from '../../../assets/miele/miele-logo.svg';
import {
  DynamicContents,
  DynamicContentsItem,
} from '../../../app-kit/components/DynamicContent/types';
import { DynamicContent } from '../../../app-kit/components/DynamicContent/DynamicContent';
import { WebLink } from '../../../app-kit/components/WebLink/WebLink';
import { WebLink as IWebLink } from '../../../app-kit/components/WebLink/types';
import { ObservedElement } from '../../../app-kit/components/ObservedElement/ObservedElement';
import { DataGetter, trackEvent } from '@rea-argonaut/analytics';

const MIELE_BRAND_COLOUR = '#8C0014';
const MIELE_BANNER_HEIGHT = '182px';
const MIELE_BANNER_HEIGHT_MOBILE = '300px';

const BannerWrapper = styled(PageContainer)`
  display: flex;
  height: ${MIELE_BANNER_HEIGHT};
  background: ${MIELE_BRAND_COLOUR};
  border-radius: ${radiusHelper('medium')};

  ${mediaHelper('mobile')} {
    height: ${MIELE_BANNER_HEIGHT_MOBILE};
    min-height: fit-content;
    flex-direction: column-reverse;
  }
`;

const ContentWrapper = styled.div`
  width: 50%;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${mediaHelper('mobile')} {
    width: 100%;
    min-width: unset;
    height: 150px;
    min-height: fit-content;
    padding: ${spacingHelper('medium')};
  }
`;

const LogoAndTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacingHelper('small')};
`;

const StyledLogo = styled.img`
  width: 110px;
  height: 20px;
`;

const StyledText = styled(DynamicContent)`
  color: ${paletteHelper('textInvertedStatic')};
  ${typographyHelper('body01')};
  text-align: center;
  max-width: 320px;
`;

const LinkWrapper = styled.div`
  padding-top: ${spacingHelper('small')};
`;

const StyledLink = styled(WebLink)`
  border-radius: ${radiusHelper('full')};
  color: ${MIELE_BRAND_COLOUR};
  background-color: ${paletteHelper('backgroundPrimary')};
  font-size: 1rem;
  line-height: 1.25rem;
  ${typographyHelper('subtitle02')};

  &:hover {
    background-color: ${paletteHelper('backgroundSecondaryBase')};
    color: ${MIELE_BRAND_COLOUR};
  }

  &:active,
  &:focus,
  &:focus-within {
    background-color: ${paletteHelper('backgroundSecondary')};
    color: ${MIELE_BRAND_COLOUR};
  }
`;

const ImageContainer = styled.div`
  display: flex;
  width: 50%;

  ${mediaHelper('mobile')} {
    width: 100%;
    height: 150px;
    border-top-right-radius: ${radiusHelper('large')};
    border-top-left-radius: ${radiusHelper('large')};
  }
`;

const StyledImage = styled.img`
  border-top-right-radius: ${radiusHelper('large')};
  border-bottom-right-radius: ${radiusHelper('large')};

  width: 100%;
  object-fit: cover;

  ${mediaHelper('mobile')} {
    border-top-right-radius: ${radiusHelper('large')};
    border-top-left-radius: ${radiusHelper('large')};
    border-bottom-right-radius: unset;
  }
`;

type TrackingContext =
  | {
      key: string;
      value: string;
    }[]
  | null;

const trackMieleBannerImpression = ({
  context,
  user_action,
}: {
  context: TrackingContext;
  user_action: 'impression' | 'click';
}) => {
  const event: DataGetter = () => ({
    schema: 'iglu:au.com.property/pca_display_ads/jsonschema/1-0-0',
    data: {
      event_name: 'pca_display_ads',
      user_action,
      type: 'banner',
      name: 'miele_display_banner',
      context: context?.map(({ key, value }) => ({ key, value })),
    },
  });

  trackEvent(event);
};

interface MieleBannerProps {
  text: DynamicContents<DynamicContentsItem>;
  link: IWebLink;
  context: TrackingContext;
}

export const MieleBanner: FunctionComponent<MieleBannerProps> = ({ text, link, context }) => {
  return (
    <ObservedElement
      onFirstIntersect={() =>
        trackMieleBannerImpression({
          context,
          user_action: 'impression',
        })
      }
    >
      <BannerWrapper>
        <ContentWrapper>
          <LogoAndTextWrapper>
            <StyledLogo alt="Miele logo image" src={logoImage} />
            <StyledText dynamicContents={text} textColorVariant="textInvertedStatic" />
          </LogoAndTextWrapper>
          <LinkWrapper>
            <StyledLink
              title={link.title}
              isExternal={link.isExternal}
              url={link.url}
              variant={link.variant}
              tracking={link.tracking}
              target="_blank"
              onClick={() => {
                trackMieleBannerImpression({
                  context,
                  user_action: 'click',
                });
              }}
            >
              {link.title}
            </StyledLink>
          </LinkWrapper>
        </ContentWrapper>
        <ImageContainer>
          <StyledImage src={bannerImage} alt="Miele banner image" loading="lazy" />
        </ImageContainer>
      </BannerWrapper>
    </ObservedElement>
  );
};
